/**
 * Dialog for creating or editing a ConversationStrategy.
 * Used in both the global admin page and scoped catalog views.
 */

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { strategiesApi } from '@/api/conversationSignals';
import type { ConversationStrategy } from '@/types/conversationSignals';

interface StrategyFormState {
  label: string;
  description: string;
}

const DEFAULT_FORM: StrategyFormState = { label: '', description: '' };

interface Props {
  open: boolean;
  strategy: ConversationStrategy | null;  // null = create mode
  scopeType?: 'stream' | 'user';
  scopeUuid?: string;
  onClose: () => void;
  onSaved: (strategy: ConversationStrategy) => void;
}

const StrategyDialog: React.FC<Props> = ({ open, strategy, scopeType, scopeUuid, onClose, onSaved }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<StrategyFormState>(DEFAULT_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setForm(strategy ? { label: strategy.label, description: strategy.description ?? '' } : DEFAULT_FORM);
      setErrors({});
    }
  }, [open, strategy]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['conversation-strategies'] });
    queryClient.invalidateQueries({ queryKey: ['conversation-signals'] });
    if (scopeType && scopeUuid) {
      queryClient.invalidateQueries({
        queryKey: ['conversation-strategies', 'scoped', scopeType, scopeUuid],
      });
    }
  };

  const createMutation = useMutation({
    mutationFn: () =>
      strategiesApi.create({
        label: form.label.trim(),
        description: form.description.trim() || undefined,
        scope_type: scopeType,
        scope_uuid: scopeUuid,
      }),
    onSuccess: (saved) => {
      invalidate();
      onSaved(saved);
    },
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      strategiesApi.update(strategy!.key, {
        label: form.label.trim(),
        description: form.description.trim() || undefined,
      }),
    onSuccess: (saved) => {
      invalidate();
      onSaved(saved);
    },
  });

  const isPending = createMutation.isPending || updateMutation.isPending;
  const isError = createMutation.isError || updateMutation.isError;

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.label.trim()) e['label'] = 'Label is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    if (strategy) updateMutation.mutate();
    else createMutation.mutate();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{strategy ? 'Edit Strategy' : 'New Strategy'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
        <TextField
          label="Label *"
          fullWidth
          value={form.label}
          onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))}
          error={!!errors['label']}
          helperText={errors['label']}
          disabled={isPending}
          autoFocus
        />
        <TextField
          label="Description"
          fullWidth
          multiline
          rows={3}
          value={form.description}
          onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
          disabled={isPending}
          helperText="When and how to apply this strategy"
        />
        {isError && <Alert severity="error">Failed to save. Please try again.</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isPending}
          startIcon={isPending ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
        >
          {isPending ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StrategyDialog;
